/// <amd-module name="VSSUI/Components/Link/Link" />
import * as React from "react";

import { FocusGroupContext } from "VSSUI/FocusGroup";
import { FocusZoneContext } from "VSSUI/FocusZone";
import { Tooltip } from "VSSUI/TooltipEx";
import { css, getSafeId, KeyCode } from "VSSUI/Util";
import { getTabIndex } from "VSSUI/Utilities/Focus";

import { ILink, ILinkProps } from "./Link.Props";

export class Link extends React.Component<ILinkProps> implements ILink {
    public static contextType = FocusGroupContext;

    private ref: React.RefObject<HTMLAnchorElement> = React.createRef();

    public render() {
        return (
            <FocusZoneContext.Consumer>
                {focusZoneContext => {
                    const { props } = this;
                    const {
                        ariaDescribedBy,
                        ariaLabel,
                        ariaExpanded,
                        ariaHasPopup,
                        ariaSelected,
                        className,
                        draggable,
                        excludeFocusZone,
                        target,
                        dataIsFocusable
                    } = props;
                    let { rel, role } = props;

                    let TagType: "a" | "span" = "a";

                    // If the link is targetting an external window or tab and no explicit rel
                    // attribute was supplied we will set noopener.
                    if (target && !rel) {
                        rel = "noopener";
                    }

                    if (!props.href && !props.role) {
                        role = "button";
                    }

                    if (!props.href) {
                        TagType = "span";
                    }

                    let link = (
                        <TagType
                            ref={this.ref}
                            aria-describedby={getSafeId(ariaDescribedBy)}
                            aria-expanded={ariaExpanded}
                            aria-haspopup={ariaHasPopup}
                            aria-label={ariaLabel}
                            aria-selected={ariaSelected}
                            className={css(className, "bolt-link", props.disabled && "disabled", props.subtle && "subtle", props.underline && "underline", props.removeUnderline && "no-underline-link")}
                            data-focuszone={!excludeFocusZone && focusZoneContext.focuszoneId}
                            data-is-focusable={dataIsFocusable}
                            download={props.download}
                            draggable={draggable}
                            href={props.href}
                            id={getSafeId(props.id)}
                            onBlur={props.onBlur}
                            onClick={this.onClick}
                            onFocus={this.onFocus}
                            onKeyPress={this.onKeyPress}
                            onMouseEnter={props.onMouseEnter}
                            onMouseLeave={props.onMouseLeave}
                            onMouseOver={props.onMouseOver}
                            onTouchEnd={props.onTouchEnd}
                            onTouchMove={props.onTouchMove}
                            onTouchStart={props.onTouchStart}
                            rel={rel}
                            role={role}
                            tabIndex={getTabIndex(this.props, this.context)}
                            target={target}
                        >
                            {this.props.children}
                        </TagType>
                    );

                    if (props.tooltipProps) {
                        link = <Tooltip {...props.tooltipProps}>{link}</Tooltip>;
                    }

                    return link;
                }}
            </FocusZoneContext.Consumer>
        );
    }

    public focus() {
        this.ref.current && this.ref.current.focus();
    }

    private handleActivation(event: React.MouseEvent<HTMLAnchorElement> | React.KeyboardEvent<HTMLAnchorElement>) {
        if (!event.defaultPrevented) {
            if (this.props.disabled) {
                event.preventDefault();
            } else {
                this.props.onClick && this.props.onClick(event);
            }
        }
    }

    private onClick = (event: React.MouseEvent<HTMLAnchorElement> | React.KeyboardEvent<HTMLAnchorElement>) => {
        this.handleActivation(event);
    };

    private onKeyPress = (event: React.KeyboardEvent<HTMLAnchorElement>) => {
        if (!this.props.href && event.which === KeyCode.enter) {
            // We only want to handle keyboard interaction if there is no href
            this.handleActivation(event);
        }
    };

    private onFocus = (event: React.FocusEvent<HTMLAnchorElement>) => {
        if (this.props.onFocus) {
            this.props.onFocus(event);
        }

        if (this.props.id) {
            this.context.onFocus(this.props.id);
        }
    };
}
